import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type SVGProps
} from 'react'
import ActionButton from 'components/ActionButton'
import ChevronIcon from 'components/ChevronIcon/ChevronIcon'
import { ReactComponent as VersionABadge } from 'assets/icons/version-a-badge.svg'
import { ReactComponent as VersionBBadge } from 'assets/icons/version-b-badge.svg'
import { ReactComponent as Attempt1Badge } from 'assets/icons/attempt-1-badge.svg'
import { ReactComponent as Attempt2Badge } from 'assets/icons/attempt-2-badge.svg'
import { ReactComponent as Attempt3Badge } from 'assets/icons/attempt-3-badge.svg'
import { ReactComponent as Attempt4Badge } from 'assets/icons/attempt-4-badge.svg'
import { ReactComponent as Attempt5Badge } from 'assets/icons/attempt-5-badge.svg'
import { ReactComponent as Attempt6Badge } from 'assets/icons/attempt-6-badge.svg'
import {
  AccordionContainer,
  ActivityName,
  Answered,
  CollapsibleContent,
  ObjectiveCard,
  ObjectiveHeader,
  ObjectiveSubtitle,
  ObjectiveTable,
  ObjectiveTableCell,
  ObjectiveDividerCell,
  ObjectiveDividerLine,
  ObjectiveDividerRow,
  ObjectiveTableHeadCell,
  ObjectiveTableRow,
  ObjectiveTitle,
  objectiveTableColumnWidths,
  AttemptBadgeIcon,
  QuizBadge,
  QuizTitleRow,
  VersionBadgeIcon,
  TableHeader,
  TablePanel,
  TableScrollContainer
} from './styles'
import { mapFormativeRow } from './assessmentService'
import ObjectivesSkeleton from './ObjectivesSkeleton'
import { DisableBlock } from 'common/common-styles'
import { FormativeRowTypes } from 'interfaces/assignments'
import {
  getActivityText,
  getProgressPercentageBars,
  getScoreText,
  getTimeSpentText
} from './shared'

type RowItem = {
  rowType:
    | 'lesson_guided_practice'
    | 'independent_practice'
    | 'continuous_practice'
    | 'formative_quiz'
  name: string
  lastActivity: string
  answeredText: string
  progressPercentage: number
  score: string
  timeSpent: string
  actionLabel: string
  actionVariant?: 'view' | 'start' | 'resume'
  disabled?: boolean
  isQuiz?: boolean
  attempt?: number | string | null
  quizVersion?: string
  isStarted?: boolean
  isCompleted?: boolean
}

type ObjectiveItem = {
  id: number | string
  formativeName?: string
  objectiveId?: number | string
  objectiveName?: string
  subtitle: string
  objectiveRows: RowItem[]
}

const FORMATIVE_SUBTITLE =
  'Read, write, discuss, and represent whole numbers up to 100,000. Representations may include numerals, and expressions.'

const ATTEMPT_BADGES: Record<
  number,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  1: Attempt1Badge,
  2: Attempt2Badge,
  3: Attempt3Badge,
  4: Attempt4Badge,
  5: Attempt5Badge,
  6: Attempt6Badge
}

/**
 * Select the dedicated attempt artwork when the API returns an attempt from
 * one through six, while keeping the existing numeric fallback for later ones.
 */
const FormativeAttemptBadge = ({
  attempt
}: {
  attempt?: number | string | null
}) => {
  const attemptNumber = Number(attempt)
  const Badge = ATTEMPT_BADGES[attemptNumber]

  if (!Badge) {
    return <QuizBadge>{attempt}</QuizBadge>
  }

  return <AttemptBadgeIcon as={Badge} aria-label={`Attempt ${attempt}`} />
}

/**
 * Match the teacher report behavior by rendering the B artwork only for an
 * explicit B version and using the A artwork for the default version.
 */
const FormativeVersionBadge = ({ version }: { version?: string }) => {
  const Badge = version === 'B' ? VersionBBadge : VersionABadge

  return (
    <VersionBadgeIcon
      as={Badge}
      aria-label={`Version ${version === 'B' ? 'B' : 'A'}`}
    />
  )
}

type FormativeObjectivesAccordionProps = {
  formatives?: unknown[]
  loading?: boolean
  onStartOrResume: (
    type: 'formatives' | 'benchmarks' | 'assignments',
    id: number
  ) => void
  focusedFormativeId?: number | null
  focusedRowType?:
    | 'lesson_guided_practice'
    | 'independent_practice'
    | 'continuous_practice'
    | 'formative_quiz'
    | null
  onFocusHandled?: () => void
}

const FormativeObjectivesAccordion = ({
  formatives = [],
  loading = false,
  onStartOrResume,
  focusedFormativeId = null,
  focusedRowType = null,
  onFocusHandled
}: FormativeObjectivesAccordionProps) => {
  const objectives = useMemo<ObjectiveItem[]>(() => {
    return formatives.map((formative: any) => {
      const objectiveRows: RowItem[] = []

      if (formative.lesson_guided_practice) {
        objectiveRows.push({
          rowType: 'lesson_guided_practice',
          ...mapFormativeRow(
            'Lesson / Guided Practice',
            formative.lesson_guided_practice
          )
        })
      }

      if (formative.independent_practice) {
        objectiveRows.push({
          rowType: 'independent_practice',
          ...mapFormativeRow(
            'Independent Practice',
            formative.independent_practice
          )
        })
      }

      if (formative.continuous_practice) {
        objectiveRows.push({
          rowType: 'continuous_practice',
          ...mapFormativeRow(
            'Continuous Practice',
            formative.continuous_practice
          )
        })
      }

      return {
        id: formative.id,
        subtitle: formative.subtitle || FORMATIVE_SUBTITLE,
        formativeName: formative?.name || '',
        objectiveId: formative.objective_id,
        objectiveName: formative.objective_name,
        objectiveRows: [
          ...objectiveRows,
          {
            rowType: 'formative_quiz',
            ...mapFormativeRow('Formative Assessment', {
              ...formative.formative_quiz,
              isAssessment: true,
              /**
               * Prefer the attempt returned on the parent formative object,
               * while preserving the nested quiz value when the endpoint
               * includes it there instead.
               */
              attempt: formative.attempt || formative.formative_quiz?.attempt,
              // the ID for formative quiz is one level above, compared to other rows
              id: formative.id
            })
          }
        ]
      }
    })
  }, [formatives])

  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [highlightedFormativeId, setHighlightedFormativeId] = useState<
    number | null
  >(null)
  const [highlightedRowType, setHighlightedRowType] =
    useState<FormativeRowTypes | null>(null)
  const objectiveRefs = useRef<Record<string, HTMLElement | null>>({})
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})

  function isAssignmentRow(key: string) {
    if (
      key === 'lesson_guided_practice' ||
      key === 'independent_practice' ||
      key === 'continuous_practice'
    ) {
      return true
    }
    return false
  }
  function startAssessment(rowKey: string, row: any) {
    if (isAssignmentRow(rowKey)) {
      return onStartOrResume('assignments', row)
    }

    return onStartOrResume('formatives', row)
  }

  useEffect(() => {
    if (!objectives.length) {
      setExpandedIds([])
      return
    }

    setExpandedIds((previousIds) => {
      const validPreviousIds = previousIds.filter((id) =>
        objectives.some((objective) => String(objective.id) === id)
      )

      if (validPreviousIds.length > 0) {
        return validPreviousIds
      }

      return [String(objectives[0].id)]
    })
  }, [objectives])

  useEffect(() => {
    if (!focusedFormativeId || !objectives.length) return

    const targetObjective = objectives.find(
      (objective) => Number(objective.id) === focusedFormativeId
    )

    if (!targetObjective) return

    const targetId = String(targetObjective.id)

    setExpandedIds((previousIds) => {
      if (previousIds.includes(targetId)) {
        return previousIds
      }

      return [...previousIds, targetId]
    })

    const scrollAndHighlight = () => {
      const objectiveElement = objectiveRefs.current[targetId]
      const targetRowType = focusedRowType || 'formative_quiz'
      const targetRowElement = rowRefs.current[`${targetId}-${targetRowType}`]

      objectiveElement?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      targetRowElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })

      setHighlightedFormativeId(focusedFormativeId)
      setHighlightedRowType(targetRowType as FormativeRowTypes)

      window.setTimeout(() => {
        setHighlightedFormativeId((currentId) =>
          currentId === focusedFormativeId ? null : currentId
        )
        setHighlightedRowType((currentType) =>
          currentType === targetRowType ? null : currentType
        )
      }, 2500)

      onFocusHandled?.()
    }

    window.setTimeout(scrollAndHighlight, 220)
  }, [focusedFormativeId, focusedRowType, objectives, onFocusHandled])

  if (loading) {
    return <ObjectivesSkeleton type='formative' />
  }

  if (!objectives.length) return null

  function shouldDisableActionButton(row: any) {
    if (row.rowType === 'formative_quiz' && row.actionLabel === 'View') {
      return true
    }

    return row.disabled
  }

  return (
    <AccordionContainer>
      {objectives.map((objective) => {
        const objectiveId = String(objective.id)
        const isExpanded = expandedIds.includes(objectiveId)

        return (
          <ObjectiveCard
            key={objective.id}
            $expanded={isExpanded}
            type='formative'
            ref={(node) => {
              objectiveRefs.current[objectiveId] = node
            }}
          >
            <ObjectiveHeader
              type='button'
              onClick={() =>
                setExpandedIds((previousIds) => {
                  if (previousIds.includes(objectiveId)) {
                    return previousIds.filter((id) => id !== objectiveId)
                  }

                  return [...previousIds, objectiveId]
                })
              }
            >
              <ChevronIcon direction={isExpanded ? 'up' : 'down'} />
              <ObjectiveTitle>{objective.objectiveName}</ObjectiveTitle>
            </ObjectiveHeader>

            <CollapsibleContent $expanded={isExpanded}>
              <ObjectiveSubtitle>{objective.subtitle}</ObjectiveSubtitle>
              <TableScrollContainer>
                <TableHeader $type='formative'>
                  <ObjectiveTable>
                    <colgroup>
                      {objectiveTableColumnWidths.map((width, index) => (
                        <col
                          key={`head-col-${objective.id}-${index}`}
                          style={{ width }}
                        />
                      ))}
                    </colgroup>
                    <thead>
                      <tr>
                        <ObjectiveTableHeadCell />
                        <ObjectiveTableHeadCell />
                        <ObjectiveTableHeadCell>
                          Questions
                          <br />
                          Answered
                        </ObjectiveTableHeadCell>
                        <ObjectiveTableHeadCell>Score</ObjectiveTableHeadCell>
                        <ObjectiveTableHeadCell>
                          Time
                          <br />
                          Spent
                        </ObjectiveTableHeadCell>
                        <ObjectiveTableHeadCell>Action</ObjectiveTableHeadCell>
                      </tr>
                    </thead>
                  </ObjectiveTable>
                </TableHeader>
                <TablePanel>
                  <ObjectiveTable>
                    <colgroup>
                      {objectiveTableColumnWidths.map((width, index) => (
                        <col
                          key={`body-col-${objective.id}-${index}`}
                          style={{ width }}
                        />
                      ))}
                    </colgroup>
                    <tbody>
                      {objective.objectiveRows.map((row, rowIndex) => {
                        const highlighted =
                          highlightedFormativeId !== null &&
                          Number(objective.id) === highlightedFormativeId &&
                          row.rowType === highlightedRowType

                        return (
                          <Fragment key={rowIndex}>
                            <ObjectiveTableRow
                              $disabled={row.disabled}
                              $highlighted={highlighted}
                              ref={(node) => {
                                rowRefs.current[
                                  `${objectiveId}-${row.rowType}`
                                ] = node
                              }}
                            >
                              <ObjectiveTableCell $alignStart>
                                {row.isQuiz ? (
                                  <QuizTitleRow $highlighted={highlighted}>
                                    <ActivityName $disabled={row.disabled}>
                                      {row.name}
                                    </ActivityName>
                                    {row.disabled ? null : (
                                      <div style={{ gap: 5, display: 'flex' }}>
                                        <FormativeAttemptBadge
                                          attempt={row.attempt}
                                        />
                                        <FormativeVersionBadge
                                          version={row.quizVersion}
                                        />
                                      </div>
                                    )}
                                  </QuizTitleRow>
                                ) : (
                                  <ActivityName $disabled={row.disabled}>
                                    {row.name}
                                  </ActivityName>
                                )}
                              </ObjectiveTableCell>

                              <ObjectiveTableCell>
                                {getActivityText(row)}
                              </ObjectiveTableCell>

                              <ObjectiveTableCell>
                                <Answered>
                                  {getProgressPercentageBars(row)}
                                </Answered>
                              </ObjectiveTableCell>

                              <ObjectiveTableCell
                                style={{
                                  paddingLeft: row?.score?.includes('%') ? 3 : 0
                                }}
                              >
                                {getScoreText(row)}
                              </ObjectiveTableCell>
                              <ObjectiveTableCell>
                                {getTimeSpentText(row)}
                              </ObjectiveTableCell>
                              <ObjectiveTableCell>
                                {shouldDisableActionButton(row) ? (
                                  <DisableBlock
                                    width={88}
                                    height={32}
                                    radius={999}
                                  />
                                ) : (
                                  <ActionButton
                                    width={84}
                                    label={row.actionLabel}
                                    onClick={() =>
                                      startAssessment(row.rowType, row)
                                    }
                                    variant={
                                      row.actionLabel === 'View'
                                        ? 'info'
                                        : 'success'
                                    }
                                  />
                                )}
                              </ObjectiveTableCell>
                            </ObjectiveTableRow>
                            {rowIndex < objective.objectiveRows.length - 1 && (
                              <ObjectiveDividerRow>
                                <ObjectiveDividerCell colSpan={6}>
                                  <ObjectiveDividerLine />
                                </ObjectiveDividerCell>
                              </ObjectiveDividerRow>
                            )}
                          </Fragment>
                        )
                      })}
                    </tbody>
                  </ObjectiveTable>
                </TablePanel>
              </TableScrollContainer>
            </CollapsibleContent>
          </ObjectiveCard>
        )
      })}
    </AccordionContainer>
  )
}

export default FormativeObjectivesAccordion
